/**
 * HeyDex's runtime in this page: a module worker built from `apps/server/src/worker.ts`.
 * The page hands it three ports in its first message, in this order: `rpc` (Effect RPC,
 * one client session per worker), `box` (the reader's box, served from this page) and
 * `http` (the runtime's HTTP routes, which the service worker relays here).
 *
 * A worker lives as long as its RPC session. When either ends (the worker fails, or the
 * session closes), the worker is terminated and a fresh one starts at once, so HTTP
 * requests always have a runtime to answer them.
 */
import { ConnectionTransientError } from "@heydex/client-runtime/connection";
import { RuntimeConnector } from "@heydex/client-runtime/rpc";
import { serveBox } from "@heydex/shared/box";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";

import { API_ORIGIN } from "@heyday/news-api/origins";
import { startBox } from "./box";
import { showBootSplash, showOpenInAnotherTab } from "./lib/bootError";

/** aws1's news API: HeyDay's record and the library's embeddings. */
const NEWS_API = import.meta.env.HDN_NEWS_API_ORIGIN ?? API_ORIGIN;

type HeaderList = ReadonlyArray<readonly [string, string]>;

/** A request to the runtime's HTTP routes, on its `http` port. The body is transferred. */
export interface RuntimeHttpRequest {
  readonly id: number;
  readonly method: string;
  readonly url: string;
  readonly headers: HeaderList;
  readonly body: ArrayBuffer | null;
}

/** The runtime's answer to a `RuntimeHttpRequest` with the same id. The body is transferred. */
export interface RuntimeHttpResponse {
  readonly id: number;
  readonly status: number;
  readonly headers: HeaderList;
  readonly body: ArrayBuffer | null;
}

/** What the service worker asks a HeyDex page, with a reply port for a `RelayReply`. */
export interface RelayRequest {
  readonly type: "heydex:fetch";
  readonly request: Omit<RuntimeHttpRequest, "id">;
}

/** A page without the runtime (another tab, or one still starting) answers `absent`. */
export type RelayReply =
  | { readonly type: "response"; readonly response: Omit<RuntimeHttpResponse, "id"> }
  | { readonly type: "absent" };

interface RunningRuntime {
  readonly worker: Worker;
  readonly rpc: MessagePort;
  readonly http: MessagePort;
  readonly stopServingBox: () => void;
  readonly pending: Map<number, (response: RuntimeHttpResponse | null) => void>;
  /** Resolves with why the worker stopped. */
  readonly stopped: Promise<string>;
  readonly stop: (reason: string) => void;
  claimed: boolean;
}

interface LocalRuntime {
  readonly readerKey: string;
  readonly box: ReturnType<typeof startBox>;
  current: RunningRuntime;
}

let runtime: LocalRuntime | null = null;
let nextRequestId = 0;

function spawn(readerKey: string, box: LocalRuntime["box"]): RunningRuntime {
  const worker = new Worker(new URL("../../server/src/worker.ts", import.meta.url), {
    type: "module",
    name: "heydex",
  });
  const rpc = new MessageChannel();
  const boxChannel = new MessageChannel();
  const http = new MessageChannel();
  const pending = new Map<number, (response: RuntimeHttpResponse | null) => void>();
  http.port1.addEventListener("message", (event: MessageEvent<RuntimeHttpResponse>) => {
    const answer = pending.get(event.data.id);
    if (answer === undefined)
      throw new Error(`HeyDex answered an unknown request ${event.data.id}`);
    pending.delete(event.data.id);
    answer(event.data);
  });
  http.port1.start();
  let stop!: (reason: string) => void;
  const stopped = new Promise<string>((resolve) => {
    stop = resolve;
  });
  const running: RunningRuntime = {
    worker,
    rpc: rpc.port1,
    http: http.port1,
    stopServingBox: serveBox(boxChannel.port1, box.host),
    pending,
    stopped,
    stop,
    claimed: false,
  };
  worker.addEventListener("error", (event) => {
    restart(running, event.message ? `HeyDex stopped: ${event.message}` : "HeyDex stopped.");
  });
  worker.addEventListener("messageerror", () => {
    restart(running, "HeyDex sent a message this page could not read.");
  });
  worker.postMessage({ type: "heydex:init", reader: readerKey, origin: location.origin, api: NEWS_API }, [
    rpc.port2,
    boxChannel.port2,
    http.port2,
  ]);
  return running;
}

function restart(running: RunningRuntime, reason: string): void {
  if (runtime === null || runtime.current !== running) return;
  running.worker.terminate();
  running.stopServingBox();
  running.rpc.close();
  running.http.close();
  for (const answer of running.pending.values()) answer(null);
  running.pending.clear();
  // A command the stopped worker started would hold the box from the next one.
  void runtime.box.host.interrupt();
  running.stop(reason);
  runtime.current = spawn(runtime.readerKey, runtime.box);
}

/** Every local store of a reader is keyed by this: hex SHA-256 of the reader id, 32 digits. */
async function readerKeyOf(readerId: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(readerId));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 32);
}

/**
 * One tab per reader runs HeyDex: its runtime and box own the reader's files. A second tab
 * shows that HeyDex is open elsewhere and takes over when that tab closes.
 */
async function holdReaderLock(readerKey: string): Promise<void> {
  const name = `heydex:${readerKey}`;
  const forever = () => new Promise<never>(() => {});
  const held = await new Promise<boolean>((resolve) => {
    void navigator.locks.request(name, { ifAvailable: true }, (lock) => {
      resolve(lock !== null);
      return lock === null ? undefined : forever();
    });
  });
  if (held) return;
  showOpenInAnotherTab();
  await new Promise<void>((resolve) => {
    void navigator.locks.request(name, () => {
      resolve();
      return forever();
    });
  });
  showBootSplash();
}

/** The runtime's routes are served by the service worker, so it must control this page. */
async function controlledByServiceWorker(): Promise<void> {
  const container = navigator.serviceWorker;
  const registration = await container.register(`${import.meta.env.BASE_URL}sw.js`, {
    scope: import.meta.env.BASE_URL,
  });
  if (container.controller !== null) return;
  const controlled = new Promise<void>((resolve) => {
    container.addEventListener("controllerchange", () => resolve(), { once: true });
  });
  // A hard reload loads past an active service worker; it takes the page when asked.
  registration.active?.postMessage("heydex:claim");
  await controlled;
}

/**
 * Starts the reader's runtime and box once this tab holds the reader's lock and the service
 * worker controls the page. Called once, after the reader session check.
 */
export async function startLocalRuntime(readerId: string): Promise<void> {
  if (runtime !== null) throw new Error("HeyDex's runtime is already running in this page.");
  // Every HeyDex page answers the service worker, from its runtime or as not running it.
  serveRuntimeRelay();
  const readerKey = await readerKeyOf(readerId);
  await holdReaderLock(readerKey);
  await controlledByServiceWorker();
  const box = startBox(readerKey);
  runtime = { readerKey, box, current: spawn(readerKey, box) };
}

function requireRuntime(): LocalRuntime {
  if (runtime === null) throw new Error("HeyDex's runtime has not started in this page.");
  return runtime;
}

/** Sends one request to the runtime's HTTP routes; a runtime that stops first answers 503. */
function fetchFromRuntime(
  request: Omit<RuntimeHttpRequest, "id">,
): Promise<Omit<RuntimeHttpResponse, "id">> {
  const running = requireRuntime().current;
  const id = nextRequestId++;
  return new Promise((resolve) => {
    running.pending.set(id, (response) =>
      resolve(
        response ?? {
          status: 503,
          headers: [["content-type", "text/plain; charset=utf-8"]],
          body: new TextEncoder().encode("HeyDex restarted before it answered.").buffer,
        },
      ),
    );
    running.http.postMessage({ ...request, id } satisfies RuntimeHttpRequest, {
      transfer: request.body === null ? [] : [request.body],
    });
  });
}

/** Answers the service worker's relay requests for as long as the page lives. */
function serveRuntimeRelay(): void {
  navigator.serviceWorker.addEventListener("message", (event: MessageEvent<RelayRequest>) => {
    if (event.data?.type !== "heydex:fetch") return;
    const reply = event.ports[0];
    if (reply === undefined) throw new Error("A relayed request arrived without a reply port.");
    if (runtime === null) {
      reply.postMessage({ type: "absent" } satisfies RelayReply);
      return;
    }
    void fetchFromRuntime(event.data.request).then((response) => {
      reply.postMessage({ type: "response", response } satisfies RelayReply, {
        transfer: response.body === null ? [] : [response.body],
      });
    });
  });
  navigator.serviceWorker.startMessages();
}

/** The runtime's RPC port for the connection supervisor: each session gets a fresh runtime. */
export const layerRuntimeConnector = Layer.succeed(
  RuntimeConnector,
  RuntimeConnector.of({
    open: Effect.acquireRelease(
      Effect.sync(() => {
        const local = requireRuntime();
        if (local.current.claimed) restart(local.current, "HeyDex started a new session.");
        local.current.claimed = true;
        return local.current;
      }),
      (running) => Effect.sync(() => restart(running, "HeyDex's session ended.")),
    ).pipe(
      Effect.map((running) => ({
        port: running.rpc,
        stopped: Effect.promise(() => running.stopped).pipe(
          Effect.flatMap((detail) =>
            Effect.fail(new ConnectionTransientError({ reason: "transport", detail })),
          ),
        ),
      })),
    ),
  }),
);
